import type { IconName } from '@risksmart-app/atomic-ui';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type {
  Access_Type_Enum,
  GetRoleAccessQuery,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { hasPermission } from 'src/rbac/hasPermission';
import type { ObjectAccess, ObjectWithContributors } from 'src/rbac/Permission';
import { useEntityPermissions } from 'src/rbac/useEntityPermissions';
import { useRoleAccess } from 'src/rbac/useRoleAccess';
import { useTypePermissions } from 'src/rbac/useTypePermissions';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type {
  ObjectToolbarCancel,
  ObjectToolbarCancelMode,
  ObjectToolbarHeader,
  ObjectToolbarProps,
  ObjectToolbarSave,
  ToolbarAction,
  ToolbarActionGroup,
  ToolbarActions,
  ToolbarActionVariant,
  ToolbarOverflow,
} from './types';
import { useObjectToolbarFormStore } from './useObjectToolbarFormStore';

/* -------------------------------------------------------------------------- */
/*  Declarative config                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A single action as declared by a consumer. Identical to the resolved
 * {@link ToolbarAction} but with an optional `permission` gate. The resolver
 * *pre-filters* denied actions out of the array before the toolbar's
 * primary/overflow split runs — it never wraps a button in `<Permission>`,
 * because an empty fragment in the action list corrupts the overflow count.
 */
export interface ToolbarActionConfig {
  id: string;
  label: string;
  iconName?: IconName;
  onClick: () => void;
  disabled?: boolean;
  variant?: ToolbarActionVariant;
  /**
   * `"action:type"` permission required to see this action (e.g.
   * `'delete:risk'`). Omit for an always-visible action. When `parentObject`
   * is set on the config, the gate is resolved instance-level; otherwise
   * type-level.
   */
  permission?: ObjectAccess;
  /**
   * Mirrors the third argument of `useHasPermissionQuery`. Only consulted on
   * the non-tRPC fallback path — grants the action when the user's role *could*
   * have access as a contributor, even without an explicit relationship. Use
   * for register-level actions, not entity-specific writes (see RSP-5818).
   */
  canHaveAccessAsContributor?: boolean;
}

export interface ToolbarActionGroupConfig {
  id: string;
  label?: string;
  actions: ToolbarActionConfig[];
}

export interface ObjectToolbarConfig {
  header: ObjectToolbarHeader;
  /**
   * The object permissions are resolved against. Its `Id` drives the
   * instance-level permission batch; pass `null`/omit for register-level
   * (type-only) toolbars.
   */
  parentObject?: ObjectWithContributors | null;
  /**
   * `'approve-cancel'` (TPP > Response) shows an Approve tick and no Save. The
   * Cancel X always navigates back (-1) — that is owned by the presentational
   * toolbar, so there is no route/onDismiss override here.
   */
  cancelMode?: ObjectToolbarCancelMode;
  /** Required when `cancelMode === 'approve-cancel'`. */
  onApprove?: () => void;
  /**
   * `true` → render the save disc. Its live state (supported / dirty / loading)
   * and the save handler come from the active object form via the toolbar bridge
   * store ({@link useObjectToolbarFormStore}), not from this config — the page
   * cannot know the form's internal dirty/submitting state. Omit/`false` → no
   * save disc.
   */
  save?: boolean;
  actions?: {
    primary?: ToolbarActionConfig[];
    overflow?: ToolbarActionGroupConfig[];
    /**
     * Download / export actions. Resolved actions are flattened into the
     * toolbar's auto-labelled "Download" group at the foot of the overflow ⋮
     * menu — declared separately here so consumers can group them by intent.
     */
    export?: ToolbarActionGroupConfig[];
    /**
     * Destructive actions (e.g. Delete). Resolved actions are flattened into
     * the toolbar's auto-labelled "Danger zone" group, always placed **last**
     * in the overflow ⋮ menu; the presentational toolbar forces them to the
     * `destructive` variant, so the per-action `variant` is not needed here.
     */
    danger?: ToolbarActionConfig[];
  };
}

export interface ResolvedObjectToolbar {
  props: ObjectToolbarProps;
  /** true while permission batches are still loading (actions fail closed). */
  loading: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Runtime inputs for the pure resolver                                      */
/* -------------------------------------------------------------------------- */

type RoleAccess = GetRoleAccessQuery['role_access'];

export interface ToolbarPermissionContext {
  trpcEnabled: boolean;
  /** `null` → permissions provider not ready; everything fails closed. */
  roleAccess: RoleAccess | null;
  userId?: string;
  hasTypePermission: (action: string, objectType: string) => boolean;
  typeLoading: boolean;
  entity: {
    canRead: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    canInsertChild: boolean;
    isLoading: boolean;
  };
}

export interface ToolbarFormContext {
  /**
   * A savable object form is currently mounted (published into the bridge
   * store). `false` → no active form (e.g. a non-saving tab), so the save disc
   * greys out.
   */
  active: boolean;
  /** The mounted form is editable (not read-only). `false` → save disc greyed. */
  supported: boolean;
  dirty: boolean;
  loading: boolean;
  /** Submits the active form. */
  onSave: () => void;
}

export interface ResolveObjectToolbarInput {
  config: ObjectToolbarConfig;
  permissions: ToolbarPermissionContext;
  form: ToolbarFormContext;
}

/* -------------------------------------------------------------------------- */
/*  Pure resolver                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Whether the current user may see an action with the given permission gate.
 * Mirrors the branching in `useHasPermissionQuery` (batch tRPC path + non-tRPC
 * `hasPermission` fallback) but synchronously, so it can be applied across an
 * array of actions without breaking the rules of hooks. Fails closed.
 */
const canSeeAction = (
  action: ToolbarActionConfig,
  permissions: ToolbarPermissionContext,
  parentObject: ObjectWithContributors | null | undefined
): boolean => {
  const { permission, canHaveAccessAsContributor } = action;

  if (!permission) {
    return true;
  }

  const [accessType, objectType] = permission.split(':');
  if (!accessType || !objectType) {
    return false;
  }

  const { trpcEnabled, roleAccess, userId, hasTypePermission, entity } =
    permissions;

  // Fail closed when the permissions provider has not resolved yet.
  if (!roleAccess) {
    return false;
  }

  if (trpcEnabled) {
    if (parentObject?.Id) {
      // `objectType` (the second half of the permission string) is intentionally
      // ignored here: the entity batch (`useEntityPermissions`) is keyed by
      // `parentObject.Id`, so the server already resolved permissions for this
      // object's real type. A caller declaring a gate whose `objectType`
      // disagrees with `parentObject`'s actual type would silently fall back to
      // this object's permission — always declare the gate against the object
      // you pass as `parentObject`.
      switch (accessType) {
        case 'read':
          return entity.canRead;
        case 'update':
          return entity.canUpdate;
        case 'delete':
          return entity.canDelete;
        case 'insert':
        case 'create':
          return entity.canInsertChild;
        default:
          return false;
      }
    }

    return hasTypePermission(accessType, objectType);
  }

  return hasPermission({
    parentObject,
    userId,
    roleAccess,
    objectType: objectType as Parent_Type_Enum,
    accessType: accessType as Access_Type_Enum,
    canHaveAccessAsContributor,
  });
};

const toResolvedAction = (action: ToolbarActionConfig): ToolbarAction => ({
  id: action.id,
  label: action.label,
  iconName: action.iconName,
  onClick: action.onClick,
  disabled: action.disabled,
  variant: action.variant,
});

/**
 * Permission-filter a flat list of action configs and map the survivors to
 * resolved actions, or `undefined` when none survive. Shared by the primary,
 * danger, and (post-flatten) download slots — each is the same filter-and-map;
 * only the source array differs.
 */
const resolveActionList = (
  actions: ToolbarActionConfig[] | undefined,
  permissions: ToolbarPermissionContext,
  parentObject: ObjectWithContributors | null | undefined
): ToolbarAction[] | undefined => {
  const allowed = (actions ?? [])
    .filter((action) => canSeeAction(action, permissions, parentObject))
    .map(toResolvedAction);

  return allowed.length > 0 ? allowed : undefined;
};

const resolveGroups = (
  groups: ToolbarActionGroupConfig[] | undefined,
  permissions: ToolbarPermissionContext,
  parentObject: ObjectWithContributors | null | undefined
): ToolbarActionGroup[] | undefined => {
  const resolved = (groups ?? [])
    .map((group) => ({
      id: group.id,
      label: group.label,
      actions: group.actions
        .filter((action) => canSeeAction(action, permissions, parentObject))
        .map(toResolvedAction),
    }))
    // Drop groups that filtered down to nothing so they don't render an empty
    // divider/heading.
    .filter((group) => group.actions.length > 0);

  return resolved.length > 0 ? resolved : undefined;
};

const resolveSave = (
  config: ObjectToolbarConfig,
  form: ToolbarFormContext
): ObjectToolbarSave | null => {
  // Approve-cancel (TPP > Response) shows an Approve tick instead of Save.
  if (config.cancelMode === 'approve-cancel') {
    return null;
  }
  if (!config.save) {
    return null;
  }

  return {
    onSave: form.onSave,
    // Grey the disc when there's nothing to save: no savable form mounted (e.g. a
    // non-saving tab), the mounted form is read-only, or the form has no pending
    // changes (not dirty). Live state comes from the bridge store.
    disabled: !form.active || !form.supported || !form.dirty,
    loading: form.loading,
    dirty: form.dirty,
  };
};

const resolveCancel = (config: ObjectToolbarConfig): ObjectToolbarCancel => ({
  mode: config.cancelMode ?? 'cancel',
  onApprove: config.onApprove,
});

const isLoading = (
  config: ObjectToolbarConfig,
  permissions: ToolbarPermissionContext
): boolean => {
  if (!permissions.trpcEnabled) {
    return false;
  }

  return config.parentObject?.Id
    ? permissions.entity.isLoading
    : permissions.typeLoading;
};

/**
 * Pure transform: declarative config + runtime permission/form/navigation
 * context → resolved {@link ObjectToolbarProps}. Holds no hooks so it can be
 * unit-tested directly.
 */
export const resolveObjectToolbarProps = ({
  config,
  permissions,
  form,
}: ResolveObjectToolbarInput): ResolvedObjectToolbar => {
  const parentObject = config.parentObject;

  // Caller-defined overflow clusters render first; `export` actions collapse
  // into the toolbar's trailing auto-labelled "Download" group — its group
  // structure is intentionally flattened (the presentational toolbar owns the
  // single "Download" heading). `danger` flattens into the auto-labelled
  // "Danger zone" group, styled `destructive` downstream.
  const groups = resolveGroups(
    config.actions?.overflow,
    permissions,
    parentObject
  );
  const download = resolveActionList(
    config.actions?.export?.flatMap((group) => group.actions),
    permissions,
    parentObject
  );
  const danger = resolveActionList(
    config.actions?.danger,
    permissions,
    parentObject
  );

  const overflow: ToolbarOverflow | undefined =
    groups || download || danger ? { groups, download, danger } : undefined;

  const actions: ToolbarActions = {
    primary: resolveActionList(
      config.actions?.primary,
      permissions,
      parentObject
    ),
    overflow,
  };

  const hasAnyAction = !!actions.primary || !!actions.overflow;

  return {
    props: {
      header: config.header,
      save: resolveSave(config, form),
      cancel: resolveCancel(config),
      actions: hasAnyAction ? actions : undefined,
    },
    loading: isLoading(config, permissions),
  };
};

/* -------------------------------------------------------------------------- */
/*  Hook                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Logic layer for the object-level toolbar. Gathers permission, form, and
 * navigation context, then resolves a declarative {@link ObjectToolbarConfig}
 * into {@link ObjectToolbarProps} for the presentational component.
 *
 * Consumed by the connected {@link ObjectToolbar}, which spreads the resolved
 * props into the presentational toolbar block.
 *
 * The resolver is memoised on `config` by reference, so callers that build the
 * config inline should wrap it in `useMemo` to keep the memo effective (the
 * transform is cheap, so this is a perf nicety, not a correctness requirement).
 * The returned `loading` flag is reserved for the Phase 1 presentational
 * component's skeleton/disabled API — actions already fail closed while
 * permissions resolve, so it is safe to ignore until then.
 */
export const useObjectToolbar = (
  config: ObjectToolbarConfig
): ResolvedObjectToolbar => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { hasTypePermission, isLoading: typeLoading } = useTypePermissions();
  const {
    canRead,
    canUpdate,
    canDelete,
    canInsertChild,
    isLoading: entityLoading,
  } = useEntityPermissions(config.parentObject?.Id);
  const roleAccess = useRoleAccess();
  const { user } = useRisksmartUser();
  // Save state is published by the active object form (mounted in a tab, below
  // the page header where this toolbar lives) via the bridge store — React
  // context can't reach up here. `null` → no savable form mounted.
  const formState = useObjectToolbarFormStore((s) => s.formState);

  return useMemo(
    () =>
      resolveObjectToolbarProps({
        config,
        permissions: {
          trpcEnabled,
          roleAccess,
          userId: user?.userId,
          hasTypePermission,
          typeLoading,
          entity: {
            canRead,
            canUpdate,
            canDelete,
            canInsertChild,
            isLoading: entityLoading,
          },
        },
        form: {
          active: formState !== null,
          supported: formState?.supported ?? false,
          dirty: formState?.dirty ?? false,
          loading: formState?.loading ?? false,
          onSave: formState?.onSave ?? (() => undefined),
        },
      }),
    [
      config,
      trpcEnabled,
      roleAccess,
      user?.userId,
      hasTypePermission,
      typeLoading,
      canRead,
      canUpdate,
      canDelete,
      canInsertChild,
      entityLoading,
      formState,
    ]
  );
};
